import { Viewer } from "../main";
import { ImageMaterial } from "../rendering";
import {
  BasicRenderTarget,
  RenderUtils
} from "../rendering";
import {
  Line3,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  Vector3,
  WebGLRenderer
} from "three";
import { MouseControls } from ".";
import onresize from "resize-event";
import { CameraImage } from "../cameras";
import { getPointScale } from "../utilities";
import { RayCaster } from "../ray-caster";

export interface IntersectResponse {
  canComplete: boolean
  canAddMore: boolean
  disableAddMore: boolean
  disableComplete: boolean
  disablePreviousImage: boolean
  disableNextImage: boolean,
}

interface Coordinate {
  position: Vector3
  direction: Vector3
}

export interface MultiImageOptions {
  canComplete: boolean
  canAddMore: boolean
  disableAddMore: boolean
  disableComplete: boolean
  disablePreviousImage: boolean
  disableNextImage: boolean
  coordinates: [Coordinate | null, Coordinate | null]
  intersect: null | IntersectResponse
}

interface WindowLoadOptions {
  closePrompt?: Function
  onComplete?: Function
  onAddMore?: Function
  canComplete: boolean
  canAddMore: boolean
}

export class MultiImageWindow {
  private viewer: Viewer;
  private cameraScene: Scene;
  public objectScene: Scene;
  private mouseControls: MouseControls;
  public camera: PerspectiveCamera
  private renderer: WebGLRenderer;
  private renderUtils: RenderUtils;
  private containerElement;
  private imageRenderMaterial: ImageMaterial
  private imageRenderTarget: BasicRenderTarget
  private setMultiImageOpen: Function;
  private setMultiImageState: Function;
  private activeCamera: CameraImage
  private tools: MultipleCameraMeasurement;
  public onComplete: Function;
  public onAddMore: Function;
  public closePrompt: Function;
  private intersectResponse = null;
  private pixelRatio = 1.0;
  private enabled = false;
  public canComplete = false;
  private canAddMore = false;
  private disablePreviousImage = false;
  private disableNextImage = false;
  public disableAddMore = false;
  public disableComplete = false;
  private cameraIndex;
  private observer;

  constructor(viewer: Viewer, props) {
    const {setMultiImageOpen, setMultiImageState} = props;

    this.viewer = viewer;
    this.setMultiImageOpen = setMultiImageOpen;
    this.setMultiImageState = setMultiImageState;

    this.initRenderer();
    this.initMaterials();
    this.initMouseEvents();

    this.mouseControls = new MouseControls(this.viewer, this.camera, 10, 90);
    this.tools = new MultipleCameraMeasurement(viewer, this);
  }

  get width(): number {
    return this.parentElement
      ? this.parentElement.offsetWidth
      : 0;
  }

  get height(): number {
    return this.parentElement
      ? this.parentElement.offsetHeight
      : 0;
  }

  get aspect() {
    return this.width / this.height;
  }

  get lookat() {
    return this.mouseControls.lookat
      .add(this.camera.position);
  }

  get parentElement() {
    return this.containerElement?.parentElement;
  }

  init(container) {
    this.containerElement = container;
    this.containerElement.appendChild(this.renderer.domElement);

    this.initResize();
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  update() {
    const state = {
      canComplete: this.canComplete,
      canAddMore: this.canAddMore,
      disableAddMore: this.disableAddMore,
      disableComplete: this.disableComplete,
      disablePreviousImage: this.disablePreviousImage,
      disableNextImage: this.disableNextImage,
      coordinates: this.tools.coordinates,
      intersect: this.intersectResponse
    } as MultiImageOptions;

    this.setMultiImageState(state);
  }

  updateIntersect() {
    this.intersectResponse = this.tools.calculate();
    this.update();
  }

  show(opts: WindowLoadOptions = {canComplete: true, canAddMore: true}) {
    const currentIndex = this.viewer.getCurrentCameraIndex();
    if (currentIndex === -1) {
      return;
    }

    this.canComplete = opts.canComplete;
    this.canAddMore = opts.canAddMore;
    this.onComplete = opts.onComplete ? opts.onComplete : () => {};
    this.onAddMore = opts.onAddMore ? opts.onAddMore : () => {};
    this.closePrompt = opts.closePrompt ? opts.closePrompt : () => {};

    if (this.enabled) {
      this.update();
      return;
    }

    this.setMultiImageOpen(true);

    // Load next camera index
    const newIndex = this.viewer.getNextCamera(currentIndex);
    this.loadCamera(newIndex);
  }

  loadNextImage() {
    const newIndex = this.viewer.getNextCamera(this.cameraIndex);
    this.loadCamera(newIndex);
  }

  loadPreviousImage() {
    const newIndex = this.viewer.getPreviousCamera(this.cameraIndex);
    this.loadCamera(newIndex);
  }

  async loadCamera(index) {
    this.cameraIndex = index;

    const nextCamera = this.viewer.cameraList.cameras[index];
    const CameraConstructor = nextCamera.constructor as any;

    this.resetSecondObservation();

    this.disablePreviousImage = true;
    this.disableNextImage = true;
    this.update();

    const newCamera = new CameraConstructor(
      nextCamera.data,
      this.mouseControls,
      this.cameraScene,
      this.camera
    );

    newCamera.setCorrectionMatrix(nextCamera.matrix);

    // Load image data from path
    await newCamera.loadCameraData();

    // Destroy previously loaded camera
    this.activeCamera?.destroy();

    // Set camera visible and apply texture
    newCamera.activateCamera();
    this.activeCamera = newCamera;

    this.updateIntersect();

    this.disablePreviousImage = false;
    this.disableNextImage = false;
    this.update();
  }

  setState(state) {
    this.enabled = state;

    if (!state) {
      this.resetObservations();
    }
  }

  close() {
    this.setMultiImageOpen(false);
  }

  initResize() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = onresize(this.parentElement, () => {
      this.handleWindowResize();
    });

    this.handleWindowResize();
  }

  initMouseEvents() {
    const element = this.renderer.domElement;
    element.addEventListener('mousedown', this.onMouseDown.bind(this), false);
    element.addEventListener('mouseup', this.onMouseUp.bind(this), false);
    element.addEventListener('mousemove', this.onMouseMove.bind(this), false);
    element.addEventListener('wheel', this.onMouseWheel.bind(this), false);
  }

  initRenderer() {
    this.renderer = new WebGLRenderer({
      alpha: true,
    });

    this.renderer.autoClear = false;
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.domElement.id = "multi-image-canvas";

    this.camera = new PerspectiveCamera(90);
    this.camera.near = 0.1;
    this.camera.far = 10;
    this.camera.up = new Vector3(0, 0, 1);

    this.cameraScene = new Scene();
    this.objectScene = new Scene();

    this.renderUtils = new RenderUtils(
      this.renderer,
      this.camera,
      this.pixelRatio
    );
  }

  initMaterials() {
    // Used for rendering panoramic as background
    this.imageRenderMaterial = new ImageMaterial();
    this.imageRenderTarget = new BasicRenderTarget(
      this.width, this.height, true);

    this.renderUtils.addTarget(this.imageRenderTarget);
  }

  onMouseDown(event) {
    event.preventDefault();
    this.mouseControls.movement = false;
    this.mouseControls.onMouseDown(event);
  }

  onMouseUp(initialEvent) {
    const event = this.mouseControls.onMouseUp(initialEvent);

    if (!this.mouseControls.upEnabled || !this.enabled || event.mouseMoved) {
      return;
    }

    const raycaster = new RayCaster(this, event);
    const direction = raycaster.vectorFromEvent(event);
    const position = this.camera.position;
    this.addSecondObservation(position, direction);
  }

  onMouseMove(initialEvent) {
    if (!this.enabled) return;

    const event = this.mouseControls.onMouseMove(initialEvent);
    if (!event.mouseMoved) return;

    this.mouseControls.updateRotation(
      this.camera.fov,
      this.width,
      this.height
    );
  }

  onMouseWheel(event) {
    if (!this.enabled) return false;

    this.mouseControls.onMouseScroll();
    this.mouseControls.onMouseWheel(event);
  }

  getClickedPoint(callback) {
    this.tools.getClickedPoint(callback);
  }

  resetObservations() {
    this.resetFirstObservation();
    this.resetSecondObservation();
  }

  resetFirstObservation() {
    this.tools.resetFirstObservation();
    this.updateIntersect();
  }

  resetSecondObservation() {
    this.tools.resetSecondObservation();
    this.updateIntersect();
  }

  addFirstObservation(position, direction) {
    this.tools.addFirstObservation(position, direction);
    this.updateIntersect();
  }

  addSecondObservation(position, direction) {
    this.tools.addSecondObservation(position, direction);
    this.updateIntersect();
  }

  handleWindowResize() {
    this.camera.aspect = this.aspect;
    this.camera.updateProjectionMatrix();

    this.renderer.domElement.style.display = "none";
    this.renderer.setSize(this.width, this.height);
    this.renderer.domElement.style.display = "block";

    // Re-render is required or else the div will flash white
    this.render();
  }

  updateMaterial(material, values) {
    let keys = Object.keys(values);
    keys.forEach(key => {
      material[key] = values[key];
    });
  }

  renderPanoramic() {
    // Render panoramic image
    this.renderUtils.setRenderTarget(this.imageRenderTarget, true);
    this.renderer.render(this.cameraScene, this.camera);

    // Update material texture with results
    this.updateMaterial(this.imageRenderMaterial, {
      colorMap: this.imageRenderTarget.texture
    });

    // Render to screen and clear depth
    this.renderUtils.renderToScreen(this.imageRenderMaterial);
    this.renderer.clearDepth();
  }

  renderSceneObjects() {
    this.renderUtils.renderScenes([this.objectScene], 1.0);
  }

  render() {
    if (!this.enabled) return false;

    this.mouseControls.update();
    this.tools.update();
    this.renderUtils.update();
    this.renderPanoramic();
    this.renderSceneObjects();
  }
};

class MultipleCameraMeasurement {
  private viewer: Viewer;
  private window: MultiImageWindow;
  public coordinates = [null, null];
  private firstPoint = null;
  private secondPoint = null;
  private sphereGeometry = new SphereGeometry(0.05, 20, 20);
  private sphereMaterial = new MeshBasicMaterial({color: 0xe6e600});

  constructor(viewer, window) {
    this.viewer = viewer;
    this.window = window;
  }

  get viewerScene() {
    return this.viewer.measurements.scene;
  }

  get windowScene() {
    return this.window.objectScene;
  }

  get sameCamera() {
    const origin1 = this.window.camera?.position;
    const origin2 = this.viewer.camera?.position;

    if (origin1 && origin2) {
      if (origin1.distanceTo(origin2) < 0.005) {
        return true;
      }
    }

    return false;
  }

  getEndpoint(origin, direction) {
    return direction
      .clone()
      .setLength(1.0)
      .add(origin);
  }

  getSphereMesh(position) {
    let sphere = new Mesh(this.sphereGeometry, this.sphereMaterial);
    sphere.position.copy(position);

    return sphere;
  }

  getClickedPoint(callback) {
    let rayA = this.coordinates[0];
    let rayB = this.coordinates[1];
    if ((rayA === null) || (rayB === null)) {
      return;
    }

    if (this.sameCamera) {
      return;
    }

    let {point, valid} = this.getIntersection(rayA, rayB);
    if (valid) {
      callback(point);
    }
  }

  setButtonDisabled(state) {
    this.window.disableComplete = state;
    this.window.disableAddMore = state;
  }

  calculate() {
    const defaultResponse = {
      error: null,
      distances: [0, 0],
      calculated: false,
      valid: true,
      message: null
    };

    if (this.sameCamera) {
      this.setButtonDisabled(true);
      return {
        ...defaultResponse,
        valid: false,
        message: "Measuring from same camera"
      };
    } else {
      this.setButtonDisabled(false);
    }

    let rayA = this.coordinates[0];
    let rayB = this.coordinates[1];
    if ((rayA === null) || (rayB === null)) {
      return defaultResponse;
    }

    let {error, distances, valid, message}
      = this.getIntersection(rayA, rayB);

    this.setButtonDisabled(!valid);

    if (!valid) {
      return {...defaultResponse, message, valid: false};
    }

    return {
      ...defaultResponse,
      error,
      distances,
      calculated: true
    };
  }

  resetFirstObservation() {
    if (this.firstPoint) {
      this.viewerScene.remove(this.firstPoint);
    }
    this.coordinates[0] = null;
    this.firstPoint = null;
  }

  resetSecondObservation() {
    if (this.secondPoint) {
      this.windowScene.remove(this.secondPoint);
    }
    this.coordinates[1] = null;
    this.secondPoint = null;
  }

  addFirstObservation(position, direction) {
    this.viewerScene.remove(this.firstPoint);

    let origin = position.clone();
    let endPoint = this.getEndpoint(origin, direction);
    this.coordinates[0] = {origin, direction};

    this.firstPoint = this.getSphereMesh(endPoint);
    this.viewerScene.add(this.firstPoint);
    this.updatePointScale();
  }

  addSecondObservation(position, direction) {
    this.windowScene.remove(this.secondPoint);

    let origin = position.clone();
    let endPoint = this.getEndpoint(origin, direction);
    this.coordinates[1] = {origin, direction};

    this.secondPoint = this.getSphereMesh(endPoint);
    this.windowScene.add(this.secondPoint);
    this.updatePointScale();
  }

  setPointScale(mesh) {
    const scale = getPointScale(this.viewer.camera, mesh);
    mesh.scale.set(scale, scale, scale);
    mesh.geometry.computeBoundingSphere();
  }

  updatePointScale() {
    if (this.firstPoint) {
      this.setPointScale(this.firstPoint);
    }

    if (this.secondPoint) {
      this.setPointScale(this.secondPoint);
    }
  }

  /** Get closest possible midpoint for two line segments */
  getIntersection(rayA, rayB) {
    let message = "";
    let valid = true;

    let Nv = rayA.direction.clone().cross(rayB.direction);
    let Na = rayA.direction.clone().cross(Nv).normalize();
    let Nb = rayB.direction.clone().cross(Nv).normalize();
    let Da = rayA.direction.clone().normalize();
    let Db = rayB.direction.clone().normalize();
    let da = rayB.origin.clone().sub(rayA.origin).dot(Nb) / Da.dot(Nb);
    let db = rayA.origin.clone().sub(rayB.origin).dot(Na) / Db.dot(Na);
    let ptA = rayA.origin.clone().add(Da.multiplyScalar(da));
    let ptB = rayB.origin.clone().add(Db.multiplyScalar(db));

    // Get our midpoint and segment length
    let lineSegment = new Line3(ptA, ptB);
    let segmentLength = lineSegment.distance();
    let midPoint = new Vector3();
    lineSegment.getCenter(midPoint);

    // Make sure our result is in front of us
    const d1n = ptA.clone().sub(rayA.origin).setLength(1.0);
    const d2n = ptB.clone().sub(rayB.origin).setLength(1.0);
    const a1 = Math.abs(rayA.direction.angleTo(d1n));
    const a2 = Math.abs(rayB.direction.angleTo(d2n));
    const behindCamera = (a1 > 0.005) || (a2 > 0.005);
    if (behindCamera) {
      valid = false;
      message = "Intersection point behind camera";
    }

    let distances = [
      ptA.distanceTo(rayA.origin),
      ptB.distanceTo(rayB.origin)
    ];

    return {
      point: midPoint,
      error: segmentLength,
      distances,
      valid,
      message
    };
  }

  update() {
    this.updatePointScale();
  }
}